import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { WaTabKey } from './ChannelTabs';
import {
    selectWaBlocksSendToAll, selectWaLegState, selectWaSetupIncomplete, selectWaUnmapped, WaRootState,
} from './selectors';

// SmartSend part B — the sticky action bar, rendered ONLY in tabs mode. With the add-on off the
// screen keeps its original in-body action row, untouched (14-W3 §2: "כבוי ⇒ זהה פיקסלית").
//
// It is a generalisation of the save bar the screen already has at `SmartSendScreen.tsx:816-824`
// — `position: sticky; bottom: 0`, same clearance. Sticky and NOT fixed, so it stays inside the
// RTL content column and never overlaps the side menu.
//
// ⚠️ BUILD CHECK (14-W3 §1): `position: sticky` is cancelled by ANY ancestor with an `overflow`
// other than `visible`. Verified for this tree: `DefaultScreen` → `.containerBody` sets no
// overflow, and neither does `.management`. If that ever changes the bar silently becomes an
// in-flow element at the bottom of the document — which is a degradation, not a break, but it
// must be noticed rather than discovered.

const useStyles = makeStyles((theme) => ({
    bar: {
        position: 'sticky', bottom: 0, zIndex: 5, marginTop: theme.spacing(3),
        display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), flexWrap: 'wrap',
        padding: theme.spacing(1.5, 2), borderRadius: 6,
        // 🔴 PHYSICAL left, not paddingInlineStart, and not flipped by jss-rtl.
        // The accessibility widget is anchored bottom-LEFT in BOTH directions, and the left end of
        // this bar is where the autosave state lives — "וואטסאפ · ✓ נשמר", and on failure the only
        // notice that the mapping did NOT save. The widget covered exactly that. 96px is the
        // widget's own footprint plus a gutter; nothing load-bearing may render under it.
        paddingLeft: 96,
        background: 'var(--pls-d-surface, #fff)', border: '1px solid var(--pls-d-border, #e0e0e0)',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
    },
    state: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
    hint: { color: 'var(--pls-d-text-2, #5b6b7b)' },
}));

interface Props {
    activeTab: WaTabKey;
    canSend: boolean;
    saving: boolean;
    emailSaveState: 'idle' | 'saving' | 'saved' | 'error' | 'locked';
    // ⚠️ CORRECTED 2026-09-22. The rule below is real, but it never applied to THIS prop.
    // `onTestSend` is optional in the type and the withhold-branch is written for it, yet the
    // single call site — SmartSendScreen.tsx:1314, the only <ActionBar> in the tree — passes it
    // UNCONDITIONALLY at :1322. Both channels have a test send; the handler just picks which
    // dialog to open. So the undefined branch is unreachable today. Left optional on purpose:
    // it is the shape a third channel without a test send would need, and narrowing it now
    // would have to be widened again.
    onTestSend?: () => void;
    // 🔴 THIS one is the live case, and it is where the rule actually earns its keep: a control
    // that cannot act is WITHHELD — not disabled, not greyed (48 §2 rule 1). `onPreview` opens
    // the E-MAIL HTML preview, which a stand-alone WhatsApp container does not have; its message
    // is previewed in the phone on the mapping screen. SmartSendScreen passes it only while the
    // e-mail tab is on screen — never on the WhatsApp tab or the WhatsApp unit's own page — and
    // otherwise the branch below withholds the button.
    onPreview?: () => void;
    onSendToAll: () => void;
    // 12-fix-wave 7.4(g) — the send is closed (a channel of it went out): the WhatsApp test send is
    // disabled. The e-mail tab's test send is not affected.
    // QA BUG-06 — and the D1 text is then the hover reason of a disabled WhatsApp test send and «שלח לכולם».
    waTestClosed?: boolean;
}

const ActionBar: React.FC<Props> = ({
    activeTab, canSend, saving, emailSaveState, onPreview, onTestSend, onSendToAll, waTestClosed = false,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const hasLeg = useSelector((s: any) => !!s.smartSendWa.waMapping?.HasLeg);
    const waSaveState = useSelector((s: any) => s.smartSendWa.waSaveState);
    // QA BUG-25 — the unsaved work the WhatsApp autosave debounces on (useWaAutosave `somethingUnsaved`):
    // while there is any, the WhatsApp half never reads «כל השינויים נשמרו». `emailSaveState` comes already
    // corrected the same way (SmartSendScreen `shownSaveState`).
    const waUnsaved = useSelector((s: any) => !!s.smartSendWa.waDirty || !!s.smartSendWa.suppressEmailDirty);
    const legState = useSelector((s: WaRootState) => selectWaLegState(s));
    // "שלח לכולם" is disabled in EVERY state where the e-mail is already out — the waiting one
    // AND the locked one (v1.10 m08). The main send would answer 404 CAMPAIGN_NOT_FOUND there,
    // a code that is not in §4 and has no i18n key, so the operator would see nothing useful.
    const emailAlreadySent = useSelector((s: WaRootState) => selectWaBlocksSendToAll(s));
    // 🔴 C-29 — the intent is "WhatsApp only" and there is no leg yet. `canSend` is already
    // false (it needs `HasLeg ∧ HasHeader`), so the button is refused; without a reason on
    // screen that refusal is indistinguishable from a broken button.
    const setupIncomplete = useSelector((s: WaRootState) => selectWaSetupIncomplete(s));
    // QA BUG-13 (W-12, WO-07) — a WhatsApp variable is still unmapped. `selectWaCanSend` refuses the send
    // on this same list, so the hint below names the reason with the C-29 sentence. Read only in leg state
    // `normal` (unit read, header live, unit Status = 1); the closed and e-mail-out states keep their own
    // reasons (D1 tooltip, 4.4 hints — the `emailAlreadySent` arm comes first), and before the unit's GET
    // answers there is no map to judge. A number, not the array: a fresh array re-renders on every dispatch.
    const waUnmappedCount = useSelector((s: WaRootState) => selectWaUnmapped(s).length);
    // The tooltip portals to document.body, outside App's <div dir> (same as TokenMappingTable).
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);

    // The hint says WHICH reason applies. In the waiting state the one available action is the
    // retry in the WhatsApp tab; in the locked state nothing is; and in the incomplete state the
    // way out is to finish the WhatsApp message in its tab — wait for the unit to be read (C-29),
    // or map the variables that are still unmapped (QA BUG-13).
    //
    // ⚠️ ORDER MATTERS: "the mail has already gone out" outranks "the setup is unfinished",
    // because the first is terminal and the second is an instruction the operator can act on.
    // Offering an action that cannot change anything is worse than offering none.
    const sendHint = emailAlreadySent
        ? t(legState === 'waiting'
            ? 'DataSources.send.whatsapp.mapping.waPending'
            : 'DataSources.send.whatsapp.mapping.lockedAfterSend')
        : ((setupIncomplete || (legState === 'normal' && waUnmappedCount > 0))
            ? t('DataSources.send.whatsapp.mapping.waOnlySetupIncomplete') : '');

    // 🔴 A test send is a REAL, CHARGED send with no sandbox (invariant 9). Without a leg the
    // server answers 404 WA_NO_LEG before `EnsureTestHeader`, so the button is disabled rather
    // than offering an action that cannot work.
    const testDisabled = saving
        || (activeTab === 'whatsapp' && (!hasLeg || legState === 'chargedNoHeader' || legState === 'headerDeleted' || waTestClosed));
    const sendDisabled = !canSend || saving || emailAlreadySent;

    // QA BUG-06 (C-06) — a disabled button fires no mouse events, so the D1 reason hovers on a wrapping
    // span (the SourcePicker / CampaignPicker idiom). Only while the send is closed AND the button is
    // disabled; every other state renders the bare button, as before.
    const closedTip = (disabled: boolean, button: React.ReactElement) => (disabled && waTestClosed
        ? (
            <Tooltip title={t('DataSources.send.errors.sendClosed')} PopperProps={{ dir: isRTL ? 'rtl' : 'ltr' }}>
                <span style={{ display: 'inline-flex' }}>{button}</span>
            </Tooltip>
        )
        : button);

    return (
        <Box className={classes.bar}>
            {onPreview && (
                <Button variant="outlined" color="primary" onClick={onPreview}>
                    {t('DataSources.send.actions.preview')}
                </Button>
            )}
            {onTestSend && closedTip(testDisabled && activeTab === 'whatsapp', (
                <Button variant="outlined" color="primary" disabled={testDisabled} onClick={onTestSend}>
                    {t('DataSources.send.actions.testSend')}
                </Button>
            ))}
            {closedTip(sendDisabled, (
                <Button
                    variant="contained"
                    color="primary"
                    disabled={sendDisabled}
                    onClick={onSendToAll}
                >
                    {t('DataSources.send.actions.sendToAll')}
                </Button>
            ))}
            {sendHint && (
                <Typography variant="body2" className={classes.hint}>{sendHint}</Typography>
            )}

            <Box style={{ flex: 1 }} />

            {/* Both channels' save state, in one place: the operator edits two maps on one
                screen and a single indicator could only ever describe one of them. */}
            <Box className={classes.state} aria-live="polite">
                {(emailSaveState === 'saving' || waSaveState === 'saving') && <CircularProgress size={16} />}
                {emailSaveState === 'saving' && (
                    <Typography variant="body2" className={classes.hint}>
                        {`${t('DataSources.send.whatsapp.tabs.email')} · ${t('DataSources.send.autosave.saving')}`}
                    </Typography>
                )}
                {emailSaveState === 'saved' && (
                    <Typography variant="body2" style={{ color: 'var(--pls-d-success, #33691e)', fontWeight: 600 }}>
                        {`${t('DataSources.send.whatsapp.tabs.email')} · ✓ ${t('DataSources.send.autosave.saved')}`}
                    </Typography>
                )}
                {emailSaveState === 'error' && (
                    <Typography variant="body2" style={{ color: 'var(--pls-d-danger, #8a1c1c)', fontWeight: 600 }}>
                        {`${t('DataSources.send.whatsapp.tabs.email')} · ${t('DataSources.send.autosave.saveFailed')}`}
                    </Typography>
                )}
                {waSaveState === 'saved' && !waUnsaved && (
                    <Typography variant="body2" style={{ color: 'var(--pls-d-success, #33691e)', fontWeight: 600 }}>
                        {`${t('DataSources.send.whatsapp.tabs.whatsapp')} · ✓ ${t('DataSources.send.autosave.saved')}`}
                    </Typography>
                )}
                {waSaveState === 'error' && (
                    <Typography variant="body2" style={{ color: 'var(--pls-d-danger, #8a1c1c)', fontWeight: 600 }}>
                        {`${t('DataSources.send.whatsapp.tabs.whatsapp')} · ${t('DataSources.send.autosave.saveFailed')}`}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default ActionBar;
